import { useCallback, useEffect, useState } from "react";

export function useSignatureController(containerSelector: string) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentFieldId, setCurrentFieldId] = useState<string | null>(null);
  const [signatureValue, setSignatureValue] = useState<string>("");

  const openModal = useCallback((fieldId: string) => {
    setCurrentFieldId(fieldId);
    const container = document.querySelector(containerSelector);
    if (container) {
      const byId = container.querySelector(`#${CSS.escape(fieldId)}`) as (HTMLInputElement | HTMLTextAreaElement) | null;
      const byDataId = container.querySelector(`[data-id="${CSS.escape(fieldId)}"]`) as (HTMLInputElement | HTMLTextAreaElement) | null;
      const byDataFieldId = container.querySelector(`[data-field-id="${CSS.escape(fieldId)}"]`) as (HTMLInputElement | HTMLTextAreaElement) | null;
      const field = byId || byDataId || byDataFieldId;
      let val = field ? (field as HTMLInputElement | HTMLTextAreaElement).value : "";
      if (!val) {
        const display = container.querySelector(`[data-signature-field="${CSS.escape(fieldId)}"]`) as HTMLElement | null;
        const img = display?.querySelector('img.signature-img') as HTMLImageElement | null;
        if (img && img.src && img.src.startsWith('data:image/')) {
          val = img.src;
        }
      }
      setSignatureValue(val || "");
    }
    setIsOpen(true);
  }, [containerSelector]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setCurrentFieldId(null);
    setSignatureValue("");
  }, []);

  const saveSignature = useCallback((signature: string) => {
    if (!currentFieldId) return;
    const container = document.querySelector(containerSelector);
    if (!container) return;
    const byId = container.querySelector(`#${CSS.escape(currentFieldId)}`) as (HTMLInputElement | HTMLTextAreaElement) | null;
    const byDataId = container.querySelector(`[data-id="${CSS.escape(currentFieldId)}"]`) as (HTMLInputElement | HTMLTextAreaElement) | null;
    const byDataFieldId = container.querySelector(`[data-field-id="${CSS.escape(currentFieldId)}"]`) as (HTMLInputElement | HTMLTextAreaElement) | null;
    const field = byId || byDataId || byDataFieldId;
    if (field) {
      (field as HTMLInputElement | HTMLTextAreaElement).value = signature;
      field.dispatchEvent(new Event("input", { bubbles: true }));
      field.dispatchEvent(new Event("change", { bubbles: true }));
      const signatureDisplay = container.querySelector(
        `[data-signature-field="${CSS.escape(currentFieldId)}"]`
      ) as HTMLElement | null;
      if (signatureDisplay) {
        if (signature && signature.startsWith("data:image/")) {
          signatureDisplay.innerHTML = `
            <img class="signature-img" src="${signature}" alt="Signature"/>
            <div class="signature-edit-icon" aria-label="Edit signature" title="Edit">✎</div>
          `;
        } else {
          signatureDisplay.innerHTML = `
            <div class="signature-placeholder">
              <div class="signature-icon">✍️</div>
              <div>Click to sign</div>
            </div>
          `;
        }
      }
    }
    setIsOpen(false);
    setCurrentFieldId(null);
    setSignatureValue("");
  }, [containerSelector, currentFieldId]);

  return { isOpen, currentFieldId, signatureValue, setSignatureValue, openModal, closeModal, saveSignature };
}

export function useSignatureFieldHandlers(
  containerSelector: string,
  onOpen: (fieldId: string) => void,
  sectionIndex?: number,
  subsectionIndex?: number
) {
  useEffect(() => {
    let container = document.querySelector(containerSelector) as Element | null;
    let containerObserver: MutationObserver | null = null;
    let waitingObserver: MutationObserver | null = null;

    const handleSignatureFieldClick = (e: Event) => {
      const target = e.target as HTMLElement;
      let fieldId = target.id;
      if (!fieldId && target.dataset && target.dataset.signatureField) {
        fieldId = target.dataset.signatureField;
      }
      if (!fieldId) {
        let current = target;
        while (current && container && current !== container) {
          if (current.dataset && current.dataset.signatureField) {
            fieldId = current.dataset.signatureField;
            break;
          }
          current = current.parentElement as HTMLElement;
        }
      }
      if (fieldId) {
        e.preventDefault();
        e.stopPropagation();
        onOpen(fieldId);
      }
    };

    const attachForContainer = (host: Element) => {
      const allInputs = host.querySelectorAll("input, textarea");
      const signatureFields: (HTMLInputElement | HTMLTextAreaElement)[] = [];
      allInputs.forEach((input) => {
        const id = (input.getAttribute("id") || "").toLowerCase();
        const dataId = (input.getAttribute("data-id") || "").toLowerCase();
        const dataType = (input.getAttribute("data-type") || "").toLowerCase();
        const dataFieldId = (input.getAttribute("data-field-id") || "").toLowerCase();
        if (id.includes("signature") || dataId.includes("signature") || dataType === "signature" || dataFieldId.includes("signature")) {
          signatureFields.push(input as HTMLInputElement | HTMLTextAreaElement);
        }
      });

      signatureFields.forEach((field) => {
        if (field.dataset && field.dataset.signatureProcessed) return;
        field.dataset.signatureProcessed = "true";
        if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
          field.readOnly = true;
          field.style.display = "none";
        }

        const signatureDisplay = document.createElement("div");
        signatureDisplay.className = "signature-display";
        const dataIdVal = field.getAttribute("data-id") || "";
        const dataFieldIdVal = field.getAttribute("data-field-id") || "";
        const fieldIdAttr = field.getAttribute("id") || "";
        const sigKey = fieldIdAttr || dataFieldIdVal || dataIdVal;
        signatureDisplay.dataset.signatureField = sigKey;


        if (field.nextSibling) {
          field.parentElement?.insertBefore(signatureDisplay, field.nextSibling);
        } else {
          field.parentElement?.appendChild(signatureDisplay);
        }

        const updateDisplay = () => {
          const val = (field as HTMLInputElement | HTMLTextAreaElement).value || "";
          if (val && val.startsWith("data:image/")) {
            signatureDisplay.innerHTML = `
                <img class="signature-img" src="${val}" alt="Signature"/>
                <div class="signature-edit-icon" aria-label="Edit signature" title=" Edit">✎</div>
              `;
          } else {
            signatureDisplay.innerHTML = `
              <div class="signature-placeholder">
                <div class="signature-icon">✍️</div>
                <div>Click to sign</div>
              </div>
            `;
          }
        };
        updateDisplay();

        field.addEventListener("click", handleSignatureFieldClick);
        field.addEventListener("focus", handleSignatureFieldClick);
        // Keep signature display in sync with input value updates (prefill, edits)
        field.addEventListener("input", updateDisplay);
        field.addEventListener("change", updateDisplay);
        signatureDisplay.addEventListener("click", handleSignatureFieldClick);
      });

      return signatureFields;
    };

    // Attach now or wait for container to appear
    let cleanupFields: (HTMLInputElement | HTMLTextAreaElement)[] = [];
    if (container) {
      cleanupFields = attachForContainer(container);
      containerObserver = new MutationObserver(() => {
        if (container) attachForContainer(container);
      });
      containerObserver.observe(container, { childList: true, subtree: true });
    } else {
      waitingObserver = new MutationObserver(() => {
        const found = document.querySelector(containerSelector) as Element | null;
        if (found) {
          container = found;
          cleanupFields = attachForContainer(found);
          containerObserver = new MutationObserver(() => {
            attachForContainer(found);
          });
          containerObserver.observe(found, { childList: true, subtree: true });
          if (waitingObserver) waitingObserver.disconnect();
          waitingObserver = null;
        }
      });
      waitingObserver.observe(document.documentElement, { childList: true, subtree: true });
    }

    return () => {
      if (containerObserver) containerObserver.disconnect();
      if (waitingObserver) waitingObserver.disconnect();
      cleanupFields.forEach((field) => {
        field.removeEventListener("click", handleSignatureFieldClick);
        field.removeEventListener("focus", handleSignatureFieldClick);
        field.removeEventListener("input", () => { });
        field.removeEventListener("change", () => { });
        const signatureDisplay = field.parentElement?.querySelector(
          ".signature-display"
        );
        if (signatureDisplay) {
          signatureDisplay.removeEventListener("click", handleSignatureFieldClick);
        }
      });
    };
  }, [containerSelector, onOpen, sectionIndex, subsectionIndex]);
}
